import React, { useMemo, useState } from "react";
import { Empty, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import GraphicStand from 'components/graphic-stand';
import GraphicWarehouse from "components/graphic-warehouse";
import { PageWrapDefault } from "components/layout";
import { Localize } from "components/service";
import { useActiveMenuItem, useBreadCrumbs, useDebounce, useTranslation } from "components/use-hooks";
import { gqlBuilderWhere } from "utils";
import { FILTER_FIELDS } from "components/graphic-stand/graphic-stand-const";
import "./graphic-warehouse-shelves.css";

const filterTable = 'table-graphic-warehouse-shelves';
const objOrderBy = GraphicWarehouse.Helpers.ShelfAvailability.GRAPHIC_WAREHOUSE_ORDER_BY;
const shelfFilterFields = [
    FILTER_FIELDS.SHELF_NUMBER,
    FILTER_FIELDS.EXHIBITOR,
    FILTER_FIELDS.EXHIBITION_EVENT,
    FILTER_FIELDS.STAND_NUMBER
];

const ShelfTooltipContent = ({ label, stands }) => {
    if (!stands?.length)
    {
        return <Localize>WAREHOUSE.Label_Empty</Localize>;
    }

    return (
        <div className="warehouse-shelf-tooltip">
            <strong className="warehouse-shelf-tooltip-title">
                <Localize>TABLES.Column_Title_Shelf</Localize>: { label }
            </strong>
            { stands.map((stand) => (
                <div
                    className="warehouse-shelf-tooltip-row"
                    key={ stand.id }
                >
                    <strong>{ stand.exhibitor || <>&mdash;</> }</strong>
                    <span>{ stand.exhibitionEvent?.title || <>&mdash;</> }</span>
                    <span><Localize>TABLES.Column_Title_Stands</Localize>: { stand.stand_number || <>&mdash;</> }</span>
                </div>
            )) }
        </div>
    );
};

const GraphicWarehouseShelvesPage = () => {
    useActiveMenuItem([ "graphic-warehouse-shelves" ], [ "graphic-storage" ]);
    const translations = useTranslation();

    const breadcrumbs = [
        {
            label: translations[ "BREADCRUMBS.Label_ShelfUtilization" ] || "Shelf utilization",
            path: `/graphic-shelves`
        }
    ]

    useBreadCrumbs(breadcrumbs);

    const graphicWarehouseFilters = JSON.parse(localStorage.getItem(`graphicWarehouseFilters-${ filterTable }`));
    const graphicWarehouseSearch = JSON.parse(localStorage.getItem(`graphicWarehouseSearch-${ filterTable }`));

    const [ searchText, setSearchText ] = useState(graphicWarehouseSearch);
    const [ filters, setFilters ] = useState(gqlBuilderWhere(graphicWarehouseFilters));
    const [ , setResetSorting ] = useState(false);
    const debouncedSearchText = useDebounce(searchText);
    const hasActiveFilter = Boolean(searchText) || Boolean(filters);

    const { where: objFilter, whereEvents } = GraphicStand.Helpers.Filter.separateFilters({
        AND: [
            ...(!filters ? [] : Array.isArray(filters) ? filters : [ filters ])
        ]
    });

    const filteredVariables = useMemo(() => ({
        text: debouncedSearchText,
        ...(objFilter ? { where: objFilter } : {}),
        ...(whereEvents ? { whereEvents } : {})
    }), [ debouncedSearchText, objFilter, whereEvents ]);

    const {
        loading: allLoading,
        stands,
        shelfAvailability: shelfMap
    } = GraphicWarehouse.Hooks.useShelfAvailability();

    const {
        loading: filteredLoading,
        stands: filteredStands
    } = GraphicWarehouse.Hooks.useShelfAvailability({
        skip: !hasActiveFilter,
        variables: filteredVariables
    });

    const matchedShelves = useMemo(
        () => GraphicWarehouse.Helpers.ShelfAvailability.buildMatchedShelves(filteredStands),
        [ filteredStands ]
    );
    const applyFilterHighlight = hasActiveFilter && !filteredLoading;

    return (
        <PageWrapDefault
            className="page-graphic-warehouse-shelves"
            dataExist={ true }
            title={ <Localize>PAGES.Title_ShelfUtilization</Localize> }
        >
            <div className="table-action-bar">
                <div className="col">
                    <GraphicStand.Filters.Table
                        setSearchText={ setSearchText }
                        searchText={ searchText }
                        setFilters={ setFilters }
                        filters={ filters }
                        graphicStandsFilters={ graphicWarehouseFilters }
                        setResetSorting={ setResetSorting }
                        objOrderBy={ objOrderBy }
                        filterFields={ shelfFilterFields }
                        searchPlaceholder="SEARCH.Input_Placeholder_WarehouseShelves"
                        filterTable={ filterTable }
                    />
                    <div className="warehouse-shelves-counter">
                        <span><Localize>WAREHOUSE.Label_AmountShelves</Localize>: <strong>{ allLoading ? <LoadingOutlined /> : shelfMap.amountShelves }</strong></span>
                        <span><Localize>WAREHOUSE.Label_Occupied</Localize>: <strong className="occupied">{ allLoading ? <LoadingOutlined /> : shelfMap.occupiedShelves }</strong></span>
                        <span><Localize>WAREHOUSE.Label_Empty</Localize>: <strong>{ allLoading ? <LoadingOutlined /> : shelfMap.emptyShelves }</strong></span>
                    </div>
                </div>
                <div className="col">
                </div>
            </div>

            <div className="warehouse-shelves-board">
                { allLoading && !stands.length ? (
                    <div className="warehouse-shelves-loading">
                        <LoadingOutlined />
                    </div>
                ) : shelfMap.labels.length ? (
                    <div className="warehouse-shelves-grid">
                        { shelfMap.labels.map((label) => {
                            const shelfStands = shelfMap.occupiedMap.get(label) || [];
                            const occupied = shelfStands.length > 0;
                            const isExtra = shelfMap.extraLabels.includes(label);
                            const isMatched = applyFilterHighlight && matchedShelves.has(label);
                            const isDimmed = applyFilterHighlight && occupied && !isMatched;

                            return (
                                <Tooltip
                                    key={ label }
                                    title={ <ShelfTooltipContent label={ label } stands={ shelfStands } /> }
                                    placement="top"
                                >
                                    <div
                                        className={ [
                                            "warehouse-shelf-cell",
                                            occupied ? "occupied" : "empty",
                                            isExtra ? "extra" : "",
                                            isMatched ? "matched" : "",
                                            isDimmed ? "dimmed" : "",
                                        ].filter(Boolean).join(" ") }
                                    >
                                        <span className="warehouse-shelf-number">{ label }</span>
                                        { occupied &&
                                            <>
                                                <span className="warehouse-shelf-exhibitor">
                                                    { shelfStands[ 0 ]?.exhibitor || <>&mdash;</> }
                                                </span>
                                                { shelfStands.length > 1 &&
                                                    <span className="warehouse-shelf-count">{ shelfStands.length }</span>
                                                }
                                            </>
                                        }
                                    </div>
                                </Tooltip>
                            );
                        }) }
                    </div>
                ) : (
                    <div className="no-data-box">
                        <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } />
                        <span className="no-data-text"><Localize>GLOBAL.Text_NoData</Localize></span>
                    </div>
                ) }
            </div>
        </PageWrapDefault>
    );
};

export default GraphicWarehouseShelvesPage;
